<template>
  <ListViewBanner>
    <template v-if="loading.framework">
      Loading...
    </template>

    <template v-else-if="!framework">
      Unable to load framework
    </template>

    <template v-else>
      <div class="flex items-center">
        <span class="ms-3">{{ `${framework.name}` }}</span>
      </div>
    </template>
  </ListViewBanner>

  <AppContainer>
    <div class="bg-secondary-subtle text-black px-3 py-3 mb-8">
      <strong>Please Note:</strong>

      <span>
        Frameworks can be structured as a flat list or with nested goals. Use the "+ Add sub-goal" button only when a specific goal or priority requires further sub-categorization.
      </span>
    </div>

    <div class="flex items-center">
      <h2 class="text-primary ml-3 font-semibold">
        Goals
      </h2>

      <AppButton
        class="max-h-[30px] !px-0 ml-4 text-base btn-solid btn-secondary"
        @click="modalType = ModalType.AddGoal"
      >
        <FontAwesomeIcon :icon="faPlus" />
        Add
      </AppButton>
    </div>

    <!-- Search -->
    <DefaultSearch
      class="px-0"
      :archiveable="false"
      @search="searchParams = $event"
    />

    <div class="w-92 bg-white border border-gray-200 mt-5">
      <template v-if="loading.goals">
        Loading goals...
      </template>

      <template v-else-if="!goals">
        Failed to load goals
      </template>

      <template v-else>
        <template v-if="goals.length">
          <VueDraggable
            :list="goals"
            item-key="id"
            @change="handleGoalMove"
          >
            <template #item="{ element: goal }">
              <div class="border border-b-gray-200">
                <!-- Goal -->
                <template v-if="goal.archived">
                  <BadgeArchived />
                </template>

                <div
                  class="group flex justify-between ml-3 items-center text-primary px-3 py-2"
                  :class="{ 'hover:cursor-pointer': goal.subgoals.length }"
                  @click.self="goal.expanded = !goal.expanded"
                >
                  <div class="flex flex-row">
                    <!--  Only show the badge when hovered over -->
                    <BadgeReorder class="opacity-0 group-hover:opacity-100 !transition-none duration-0" />

                    <strong class="hover:cursor-text underline">
                      {{ goal.name }}
                    </strong>
                  </div>

                  <div class="ml-2 flex flex-row gap-1">
                    <FontAwesomeIcon
                      :icon="faPen"
                      class="hover:cursor-pointer text-black p-1"
                      @click="modalData = goal; modalType = ModalType.EditGoal"
                    />

                    <FontAwesomeIcon
                      :icon="faTrashCan"
                      class="hover:cursor-pointer text-black p-1"
                      @click="modalData = goal; modalType = ModalType.DeleteGoal"
                    />

                    <AppButton
                      class="rounded-md border-dashed !border-gray-400 !text-[#114F9C] btn-outline hover:bg-gray-200 text-sm !font-normal !py-1 ml-3"
                      @click="handleAddSubgoalClick(goal); goal.expanded = true"
                    >
                      <FontAwesomeIcon :icon="faPlus" />
                      {{ goal.subgoals.length ? ' Add sub-goal' : 'Add nested goal' }}
                    </AppButton>

                    <!-- Subgoal arrow expand/collapse -->
                    <FontAwesomeIcon
                      v-if="goal.subgoals.length"
                      :icon="goal.expanded ? faChevronUp : faChevronDown"
                      class="ml-2"
                      @click="goal.expanded = !goal.expanded"
                    />
                  </div>
                </div>

                <!-- Subgoals for goal -->
                <VueDraggable
                  v-if="goal.expanded"
                  :list="goal.subgoals"
                  item-key="id"
                  @change="handleSubgoalMove(goal)"
                >
                  <template #item="{ element: subgoal }">
                    <div class="group bg-gray-200 p-2 border-t border-gray-300 text-primary">
                      <div class="flex justify-between align-middle ml-4">
                        <div class="flex flex-row">
                          <!--  Only show the badge when hovered over -->
                          <BadgeReorder class="opacity-0 group-hover:opacity-100 !transition-none duration-0 !bg-white" />

                          <span class="underline">
                            {{ subgoal.name }}
                          </span>
                        </div>

                        <div class="pr-5 gap-1">
                          <FontAwesomeIcon
                            :icon="faPen"
                            class="hover:cursor-pointer text-black p-1"
                            @click="handleEditSubgoalClick(goal, subgoal)"
                          />

                          <FontAwesomeIcon
                            :icon="faTrashCan"
                            class="hover:cursor-pointer text-black p-1"
                            @click="handleDeleteSubgoalClick(goal, subgoal)"
                          />
                        </div>
                      </div>
                    </div>
                  </template>
                </VueDraggable>
              </div>
            </template>
          </Vuedraggable>
        </template>

        <template v-else>
          <div class="no-results-table-body h-80 border-0 rounded-md">
            <p class="text-center mt-6 text-secondary-emphasis text-lg">
              There are no goals
            </p>
          </div>
        </template>
      </template>
    </div>
  </AppContainer>

  <!-- Goal modals -->
  <!-- Add goal modal -->
  <template v-if="modalType === ModalType.AddGoal">
    <AppModal
      title="Add Goal"
      :disabled="processing"
      @close="modalType = null"
    >
      <template #body>
        <GoalForm
          v-model="processing"
          :model="{ sortOrder: goals.length }"
          @cancel="modalType = null"
          @submitted="addGoal"
        />
      </template>
    </AppModal>
  </template>

  <!-- Edit goal modal -->
  <template v-if="modalType === ModalType.EditGoal">
    <AppModal
      title="Edit Goal"
      :disabled="processing"
      @close="modalType = null"
    >
      <template #body>
        <GoalForm
          v-model="processing"
          :model="modalData"
          @cancel="modalType = null; modalData = null"
          @submitted="editGoal"
        />
      </template>
    </AppModal>
  </template>

  <!-- Delete goal modal -->
  <template v-if="modalType === ModalType.DeleteGoal">
    <DeleteModal
      intent="danger"
      :title="`Delete &quot;${modalData.name}&quot;`"
      :processing="processing"
      @confirm="deleteGoal"
      @close="modalType = null"
    >
      <strong>Are you sure?</strong> Deleting "{{ modalData.name }}" will also remove all associated sub-goals.
    </DeleteModal>
  </template>

  <!-- Subgoal modals -->
  <!-- Add subgoal modal -->
  <template v-if="modalType === ModalType.AddSubgoal">
    <AppModal
      title="Add sub-goal"
      :disabled="processing"
      @close="modalType = null"
    >
      <template #body>
        <!-- `modalData` contains the ID of the parent goal that this subgoal is for -->
        <GoalForm
          v-model="processing"
          type="subgoal"
          :model="modalData"
          @cancel="modalType = null"
          @submitted="addSubgoal"
        />
      </template>
    </AppModal>
  </template>

  <!-- Edit subgoal modal -->
  <template v-if="modalType === ModalType.EditSubgoal">
    <AppModal
      title="Edit sub-goal"
      :disabled="processing"
      @close="modalType = null"
    >
      <template #body>
        <GoalForm
          v-model="processing"
          type="subgoal"
          :model="modalData"
          @cancel="modalType = null; modalData = null"
          @submitted="editSubgoal"
        />
      </template>
    </AppModal>
  </template>

  <!-- Delete subgoal modal -->
  <template v-if="modalType === ModalType.DeleteSubgoal">
    <DeleteModal
      intent="danger"
      :title="`Delete &quot;${modalData.name}&quot;`"
      :processing="processing"
      @confirm="deleteSubgoal"
      @close="modalType = null"
    >
      Are you sure you want to delete "<strong>{{ modalData.name }}</strong>"?
    </DeleteModal>
  </template>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { ref, watchEffect } from 'vue';
import VueDraggable from 'vuedraggable';
import { useRoute } from 'vue-router';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faChevronDown, faChevronUp, faPen } from '@fortawesome/pro-solid-svg-icons';
import { faTrashCan, faSort } from '@fortawesome/pro-regular-svg-icons';

import { AppContainer, AppButton, AppModal } from '@/components/app';
import { BadgeArchived } from '@/components/badges';
import { ListViewBanner } from '@/components/banners';
import { GoalForm } from '@/components/forms';
import { DefaultSearch } from '@/components/forms/search';
import { DeleteModal } from '@/components/modals';

import { useHttp } from '@/composables';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { BadgeReorder } from '@/components/badges';

const ModalType = {
  AddGoal: 'AddGoal',
  EditGoal: 'EditGoal',
  DeleteGoal: 'DeleteGoal',
  AddSubgoal: 'AddSubgoal',
  EditSubgoal: 'EditSubgoal',
  DeleteSubgoal: 'DeleteSubgoal',
};

const route = useRoute();

const loading = ref({ framework: true, goals: true });
const framework = ref(null);
const goals = ref(null);
const searchParams = ref({});
const modalType = ref(null);
const modalData = ref(null);
const processing = ref(false);

const loadFramework = async () => {
  const response = await useHttp().get(`/frameworks/${route.params.framework}`);

  if (response.statusCode === HttpStatusCode.Ok) {
    framework.value = response.data;
  }

  loading.value.framework= false;
};

const loadGoals = async () => {
  loading.value.goals = true;

  const response = await useHttp().get(`/frameworks/${route.params.framework}/goals`, searchParams.value);

  if (response.statusCode === HttpStatusCode.Ok) {
    goals.value = response.data.items;
  }

  loading.value.goals = false;
};

const handleAddSubgoalClick = (goal) => {
  modalType.value = ModalType.AddSubgoal;
  modalData.value = { parent: goal.id, sortOrder: goal.subgoals.length };
};

const handleEditSubgoalClick = (goal, subgoal) => {
  modalType.value = ModalType.EditSubgoal;
  modalData.value = subgoal;
  modalData.value.parent = goal.id;
};

const handleDeleteSubgoalClick = (goal, subgoal) => {
  modalType.value = ModalType.DeleteSubgoal;
  modalData.value = subgoal;
  modalData.value.parent = goal.id;
};

const addGoal = (goalData) => {
  closeModal();

  goals.value.push(goalData);
};

const editGoal = (editedGoal) => {
  const goalIndex = goals.value.findIndex(goal => goal.id === editedGoal.id);

  goals.value[goalIndex] = { ...editedGoal, expanded: goals.value[goalIndex].expanded };

  closeModal();
};

const deleteGoal = async () => {
  processing.value = true;

  const response = await useHttp().delete(`/frameworks/${route.params.framework}/goals/${modalData.value.id}`, searchParams.value);

  if (response.statusCode === HttpStatusCode.Ok) {
    const index = goals.value.findIndex(goal => goal.id === modalData.value.id);

    goals.value.splice(index, 1);
  }

  processing.value = false;

  closeModal();
};

const addSubgoal = (newSubgoal) => {
  const goalIndex = goals.value.findIndex(goal => goal.id === newSubgoal.parent.id);

  if (goalIndex !== -1) {
    goals.value[goalIndex].subgoals.push(newSubgoal);
  }

  closeModal();
};

const editSubgoal = (editedSubgoal) => {
  const goalIndex = goals.value.findIndex(goal => goal.id === editedSubgoal.parent.id);

  if (goalIndex !== -1) {
    const subgoalIndex = goals.value[goalIndex].subgoals.findIndex(subgoal => subgoal.id === editedSubgoal.id);

    if (subgoalIndex !== -1) {
      goals.value[goalIndex].subgoals[subgoalIndex] = editedSubgoal;
    }
  }

  closeModal();
};

const deleteSubgoal = async () => {
  processing.value = true;

  const response = await useHttp().delete(`/frameworks/${route.params.framework}/goals/${modalData.value.id}`, searchParams.value);

  if (response.statusCode === HttpStatusCode.Ok) {
    const goalIndex = goals.value.findIndex(goal => goal.id === modalData.value.parent);
    const subgoalIndex = goals.value[goalIndex].subgoals.findIndex(subgoal => subgoal.id === modalData.value.id);

    goals.value[goalIndex].subgoals.splice(subgoalIndex, 1);
  }

  processing.value = false;

  closeModal();
};

const closeModal = () => {
  modalType.value = null;
  modalData.value = null;
};

const handleGoalMove = async () => {
  goals.value.forEach(async (goal, index) => {
    goal.sortOrder = index;

    const { name, description } = goal;
    const formData = { name, description, sortOrder: index };

    await useHttp().put(`/frameworks/${route.params.framework}/goals/${goal.id}`, formData);
  });
};

const handleSubgoalMove = async (goal) => {
  for (const [index, subgoal] of goal.subgoals.entries()) {
    subgoal.sortOrder = index;

    const { name, description, parent } = subgoal;
    const formData = { name, description, parent: parent.id, sortOrder: index };

    await useHttp().put(`/frameworks/${route.params.framework}/goals/${subgoal.id}`, formData);
  }
};

watchEffect(loadGoals);
loadFramework();
</script>
